import { Text, View, ScrollView, Pressable } from 'react-native';
import { Link } from 'expo-router';

import { ThemedText } from '@/components/themed-text';
import { ThemedView } from '@/components/themed-view';
import DesignExample from '@/components/design-example';
import { useColorScheme } from '@/hooks/use-color-scheme';
import { Colors, Fonts } from '@/constants/theme';

function ColorSwatch({ name, hex }: { name: string; hex: string }) {
  return (
    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12, marginBottom: 10 }}>
      <View
        style={{
          width: 40,
          height: 40,
          borderRadius: 6,
          backgroundColor: hex,
          borderWidth: 1,
          borderColor: 'rgba(201, 169, 110, 0.2)',
        }}
      />
      <View>
        <Text style={{ fontFamily: Fonts?.serifSemiBold, fontSize: 13, color: '#d4c5a0' }}>
          {name}
        </Text>
        <Text style={{ fontSize: 11, color: '#7a6f5d' }}>{hex}</Text>
      </View>
    </View>
  );
}

export default function DesignScreen() {
  const { colorScheme, toggleColorScheme } = useColorScheme();
  const scheme = colorScheme ?? 'dark';
  const palette = Colors[scheme];

  return (
    <ThemedView className="flex-1">
      <ScrollView className="flex-1" contentContainerClassName="pb-12">
        {/* Dark academia example */}
        <DesignExample />

        {/* Design System Reference */}
        <View style={{ backgroundColor: '#1a1a2e', padding: 24 }}>
          <View
            style={{
              borderBottomWidth: 1,
              borderBottomColor: 'rgba(201, 169, 110, 0.2)',
              marginBottom: 24,
              paddingBottom: 12,
            }}
          >
            <Text
              style={{
                fontFamily: Fonts?.serifSemiBold,
                fontSize: 22,
                color: '#c9a96e',
                letterSpacing: 1.2,
              }}
            >
              {'\u00A7'} Design System Reference
            </Text>
          </View>

          {/* Mode toggle */}
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: 24,
            }}
          >
            <Text
              style={{
                fontFamily: Fonts?.serifSemiBold,
                fontSize: 14,
                color: '#c9a96e',
                letterSpacing: 2,
                textTransform: 'uppercase',
              }}
            >
              Active Mode: {scheme}
            </Text>
            <Pressable
              onPress={toggleColorScheme}
              style={{
                backgroundColor: palette.accent,
                borderRadius: 8,
                paddingHorizontal: 16,
                paddingVertical: 8,
              }}
            >
              <Text style={{ fontSize: 13, fontWeight: '600', color: palette.background }}>
                {scheme === 'dark' ? '\u2600 Light' : '\u263E Dark'}
              </Text>
            </Pressable>
          </View>

          {/* Colors */}
          <View style={{ marginBottom: 32 }}>
            <Text
              style={{
                fontFamily: Fonts?.serifSemiBold,
                fontSize: 14,
                color: '#c9a96e',
                letterSpacing: 2,
                textTransform: 'uppercase',
                marginBottom: 16,
                paddingBottom: 8,
                borderBottomWidth: 1,
                borderBottomColor: 'rgba(201, 169, 110, 0.2)',
              }}
            >
              Color Palette ({scheme})
            </Text>
            <View
              style={{ backgroundColor: 'rgba(255, 255, 255, 0.03)', borderRadius: 8, padding: 16 }}
            >
              <ColorSwatch name="Background" hex={palette.background} />
              <ColorSwatch name="Surface" hex={palette.surface} />
              <ColorSwatch name="Text" hex={palette.text} />
              <ColorSwatch name="Text Muted" hex={palette.textMuted} />
              <ColorSwatch name="Accent" hex={palette.accent} />
              <ColorSwatch name="Card" hex={palette.card} />
              <ColorSwatch name="Card Text" hex={palette.cardText} />
              <ColorSwatch name="Border" hex={palette.border} />
            </View>
          </View>

          {/* Typography */}
          <View style={{ marginBottom: 32 }}>
            <Text
              style={{
                fontFamily: Fonts?.serifSemiBold,
                fontSize: 14,
                color: '#c9a96e',
                letterSpacing: 2,
                textTransform: 'uppercase',
                marginBottom: 16,
                paddingBottom: 8,
                borderBottomWidth: 1,
                borderBottomColor: 'rgba(201, 169, 110, 0.2)',
              }}
            >
              Typography
            </Text>
            <View
              style={{ backgroundColor: 'rgba(255, 255, 255, 0.03)', borderRadius: 8, padding: 16 }}
            >
              <ThemedText type="title" className="mb-2">
                Title — Cormorant Garamond Bold
              </ThemedText>
              <ThemedText type="subtitle" className="mb-2">
                Subtitle — Cormorant Garamond SemiBold
              </ThemedText>
              <ThemedText type="default" className="mb-2">
                Body — System sans-serif
              </ThemedText>
              <ThemedText type="semibold" className="mb-2">
                Semibold — System sans-serif 600
              </ThemedText>
              <ThemedText type="muted" className="mb-2">
                Muted — Secondary text
              </ThemedText>
              <ThemedText type="link">Link — Accent colored</ThemedText>
            </View>
          </View>

          {/* Card sample */}
          <View style={{ marginBottom: 32 }}>
            <Text
              style={{
                fontFamily: Fonts?.serifSemiBold,
                fontSize: 14,
                color: '#c9a96e',
                letterSpacing: 2,
                textTransform: 'uppercase',
                marginBottom: 16,
                paddingBottom: 8,
                borderBottomWidth: 1,
                borderBottomColor: 'rgba(201, 169, 110, 0.2)',
              }}
            >
              Card Component
            </Text>
            <View className="rounded-xl border border-parchment-border bg-parchment p-5">
              <Text className="mb-1 font-serif-bold text-lg text-brown-text">
                The Name of the Rose
              </Text>
              <Text className="mb-3 text-sm text-brown-muted">Umberto Eco {'\u00B7'} 1980</Text>
              <Text className="text-sm leading-5 text-brown-text">
                A medieval mystery set in an Italian monastery, weaving together semiotics, biblical
                analysis, and literary theory into a labyrinthine narrative about forbidden
                knowledge.
              </Text>
            </View>
          </View>

          {/* Navigation */}
          <Link href="/profile">
            <ThemedText type="link">{'\u2190'} Profile</ThemedText>
          </Link>
        </View>
      </ScrollView>
    </ThemedView>
  );
}
